import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { employeeService } from '@/api/domains/employee';
import {
  mapEmployeeLookupItems,
  mapEmployeeLookupItemToOption,
} from '@/api/domains/employee/mappers/employeeLookup.mapper';
import type { EmployeeLookupOption, EmployeeLookupStatus } from '@/api/domains/employee/types';
import type { SelectorOption } from '@/components/ui/inputs/selector';


export interface UseEmployeeLookupSelectorOptions {
  /** Initial employee ID to load (e.g., from URL query param) */
  initialEmployeeId?: string | null;
  /** Employee status filter */
  status?: EmployeeLookupStatus;
  /** Page size for pagination */
  pageSize?: number;
}

export interface UseEmployeeLookupSelectorReturn {
  /** Options array for Selector component */
  options: SelectorOption[];
  /** Whether initial loading is in progress */
  isLoading: boolean;
  /** Whether there are more pages to load */
  hasNextPage: boolean;
  /** Whether a load-more operation is in progress */
  isLoadingMore: boolean;
  /** Load next page of options */
  onLoadMore: () => Promise<void>;
  /** Handle search text change (server-side search) */
  onSearchChange: (searchText: string) => void;
  /** Currently selected employee info */
  selectedEmployee: {
    id: string;
    name: string;
    rut: string | null;
  } | null;
  /** Set selected employee (call when user selects from dropdown) */
  setSelectedEmployee: (employeeId: string) => void;
}

/**
 * Hook for employee lookup with pagination and server-side search.
 * Designed to work with Selector component's pagination props.
 * 
 * @example
 * ```tsx
 * const {
 *   options,
 *   isLoading,
 *   hasNextPage,
 *   isLoadingMore,
 *   onLoadMore,
 *   onSearchChange,
 *   selectedEmployee,
 *   setSelectedEmployee,
 * } = useEmployeeLookupSelector({ initialEmployeeId: '123' });
 * 
 * <Selector
 *   options={options}
 *   value={selectedEmployee?.id}
 *   onChange={(val) => setSelectedEmployee(val as string)}
 *   onLoadMore={onLoadMore}
 *   hasNextPage={hasNextPage}
 *   isLoadingMore={isLoadingMore}
 *   onSearchChange={onSearchChange}
 * />
 * ```
 */
export function useEmployeeLookupSelector({
  initialEmployeeId,
  status = 'todos',
  pageSize = 50,
}: UseEmployeeLookupSelectorOptions = {}): UseEmployeeLookupSelectorReturn {
  // Pagination state
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Search state
  const [searchText, setSearchText] = useState('');
  
  // Options accumulated from all loaded pages
  const [options, setOptions] = useState<EmployeeLookupOption[]>([]);
  
  // Selected employee (stored with full info to avoid lookup issues)
  const [selectedEmployee, setSelectedEmployeeState] = useState<{
    id: string;
    name: string;
    rut: string | null;
  } | null>(null);
  
  // Track the last processed initial employee ID to avoid re-processing
  const lastProcessedInitialId = useRef<string | null | undefined>(undefined);

  /**
   * Fetch employees from API
   */
  const fetchEmployees = useCallback(async (
    fetchPage: number,
    fetchSearchText: string,
    append: boolean,
  ) => {
    const response = await employeeService.lookup({
      page: fetchPage,
      pageSize,
      searchText: fetchSearchText || undefined,
      status,
      orderBy: 'employeeName',
      orderDirection: 'ASC',
    });

    if (!response.success || !response.data) {
      setHasNextPage(false);
      return { items: [], hasMore: false };
    }

    const responseData = response.data.data || [];
    const pagination = response.data.pagination;
    
    const mappedItems = mapEmployeeLookupItems(responseData);
    const totalPages = pagination.totalPages;
    const hasMore = fetchPage < totalPages;

    if (append) {
      setOptions(previous => [...previous, ...mappedItems]);
    } else {
      setOptions(mappedItems);
    }

    setHasNextPage(hasMore);
    return { items: mappedItems, hasMore };
  }, [pageSize, status]);

  /**
   * Load employee by ID - used for initial selection
   */
  const loadEmployeeById = useCallback(async (
    employeeId: string,
    currentOptions: EmployeeLookupOption[],
  ) => {
    // First check if already in current options
    const found = currentOptions.find(item => String(item.value) === employeeId);
    if (found) {
      setSelectedEmployeeState({
        id: String(found.value),
        name: found.label,
        rut: found.rut,
      });
      return;
    }

    // Employee not in options - search for it by ID
    const searchResponse = await employeeService.lookup({
      page: 1,
      pageSize: 1,
      searchText: employeeId,
      status,
    });
    
    const searchData = searchResponse.data?.data || [];
    if (searchResponse.success && searchData.length > 0) {
      // Se usa el mapper para que el label quede formateado igual que en fetchEmployees
      const option = mapEmployeeLookupItemToOption(searchData[0]);
      setSelectedEmployeeState({
        id: String(option.value),
        name: option.label,
        rut: option.rut,
      });

      // Add this employee to options if not present
      const alreadyInOptions = currentOptions.some(
        item => item.value === option.value,
      );
      if (!alreadyInOptions) {
        setOptions(previous => [option, ...previous]);
      }
    }
  }, [status]);

  /**
   * Initial load - fetch first page.
   * Uses ref to access latest fetchEmployees without adding it as dependency.
   * This effect intentionally runs only on mount to avoid refetching on every
   * fetchEmployees recreation (which happens when pageSize/status change).
   */
  const fetchEmployeesRef = useRef(fetchEmployees);
  fetchEmployeesRef.current = fetchEmployees;
  
  const initialEmployeeIdRef = useRef(initialEmployeeId);
  initialEmployeeIdRef.current = initialEmployeeId;

  useEffect(() => {
    const loadInitial = async () => {
      setIsLoading(true);
      const { items } = await fetchEmployeesRef.current(1, '', false);
      
      // If no initial employee ID provided, select first by default
      if (!initialEmployeeIdRef.current && items.length > 0) {
        setSelectedEmployeeState({
          id: String(items[0].value),
          name: items[0].label,
          rut: items[0].rut,
        });
        lastProcessedInitialId.current = null;
      }
      
      setIsLoading(false);
    };

    void loadInitial();
  }, []);

  /**
   * Handle initialEmployeeId changes - select the employee when ID changes
   * This handles navigation back from create/edit pages
   */
  useEffect(() => {
    // Skip if same as last processed or if loading
    if (lastProcessedInitialId.current === initialEmployeeId) return;
    if (isLoading) return;
    
    lastProcessedInitialId.current = initialEmployeeId;
    
    if (initialEmployeeId) {
      void loadEmployeeById(initialEmployeeId, options);
    } else if (options.length > 0 && !selectedEmployee) {
      // No initial ID and no selection - select first
      setSelectedEmployeeState({
        id: String(options[0].value),
        name: options[0].label,
        rut: options[0].rut,
      });
    }
  }, [initialEmployeeId, isLoading, options, selectedEmployee, loadEmployeeById]);

  /**
   * Handle search text change - reset pagination and fetch new results
   */
  const handleSearchChange = useCallback((newSearchText: string) => {
    setSearchText(newSearchText);
    setPage(1);
    setHasNextPage(true);
    
    // Fetch with new search text (non-append mode)
    void (async () => {
      setIsLoading(true);
      await fetchEmployees(1, newSearchText, false);
      setIsLoading(false);
    })();
  }, [fetchEmployees]);

  /**
   * Load next page of options (infinite scroll)
   */
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasNextPage) return;
    
    setIsLoadingMore(true);
    const nextPage = page + 1;
    
    await fetchEmployees(nextPage, searchText, true);
    setPage(nextPage);
    setIsLoadingMore(false);
  }, [isLoadingMore, hasNextPage, page, searchText, fetchEmployees]);

  /**
   * Set selected employee by ID (finds info from options)
   */
  const setSelectedEmployee = useCallback((employeeId: string) => {
    const found = options.find(option => String(option.value) === employeeId);
    if (found) {
      setSelectedEmployeeState({
        id: String(found.value),
        name: found.label,
        rut: found.rut,
      });
    }
  }, [options]);

  /**
   * Convert internal options to SelectorOption format.
   *
   * El colaborador seleccionado se mantiene siempre en la lista aunque quede fuera del
   * resultado vigente: al elegir una opción el Selector limpia su búsqueda, lo que dispara
   * `onSearchChange('')` y recarga la primera página sin filtro. Como el Selector resuelve la
   * etiqueta buscando el valor dentro de `options`, sin esta garantía mostraría el placeholder
   * pese a que la selección sigue vigente.
   */
  const selectorOptions: SelectorOption[] = useMemo(() => {
    const mappedOptions = options.map(option => ({
      value: String(option.value),
      label: option.label,
      supportingText: option.rut || undefined,
    }));

    if (!selectedEmployee) return mappedOptions;
    if (mappedOptions.some(option => option.value === selectedEmployee.id)) return mappedOptions;

    return [
      {
        value: selectedEmployee.id,
        label: selectedEmployee.name,
        supportingText: selectedEmployee.rut || undefined,
      },
      ...mappedOptions,
    ];
  }, [options, selectedEmployee]);

  return {
    options: selectorOptions,
    isLoading,
    hasNextPage,
    isLoadingMore,
    onLoadMore: handleLoadMore,
    onSearchChange: handleSearchChange,
    selectedEmployee,
    setSelectedEmployee,
  };
}

export default useEmployeeLookupSelector;
